<template>
  <div class="v3-form limit-active-pass-form">
    <div class="row">
      <div>
        <Label
          html-for="lap-limit-input"
          label-id="lap-limit-input-lbl"
          required
        >
          Set limit
        </Label>
        <InputField
          id="lap-limit-input"
          label-id="lap-limit-input-lbl"
          v-model="state.form.limit"
          placeholder="No limit"
          :disabled="!isFormEditable"
          class="d-block"
          @input="setLimit($event.target.value)"
          :invalid-feedback="errors.limit || state.serverErrors.limit"
        ></InputField>
      </div>
      <fieldset class="mt-4 border-0 p-0 m-0">
        <legend class="v3-form-label mb-2 float-none w-100">
          Apply limit for
        </legend>
        <div class="mt-2 d-block" style="text-align: left">
          <CustomCheck
            v-model="state.form.student_pass"
            label="STU Passes initiated by the student"
            :disabled="!isFormEditable"
            @change="state.form.student_pass = !state.form.student_pass"
            :invalid-feedback="
              errors.student_pass || state.serverErrors.student_pass
            "
          ></CustomCheck>
        </div>
        <div class="mt-3 d-block" style="text-align: left">
          <CustomCheck
            v-model="state.form.proxy_pass"
            label="TCH (PRX) Passes"
            :disabled="!isFormEditable"
            @change="state.form.proxy_pass = !state.form.proxy_pass"
            :invalid-feedback="
              errors.proxy_pass || state.serverErrors.proxy_pass
            "
          ></CustomCheck>
        </div>
        <div
          v-if="state.form.proxy_pass"
          class="my-3 p-2 d-flex align-items-center justify-content-between lap-teacher-override-row"
          style="background-color: #f9fafb; border-radius: 8px"
        >
          <div id="lap-teacher-override-label">
            Allow teachers to override the limit<br />
            <span class="lap-teacher-override-hint">
              (they will get an alert first)</span
            >
          </div>
          <div>
            <CustomSwitch
              id="lap-teacher-override-switch"
              aria-labelledby="lap-teacher-override-label"
              :model-value="state.form.teacher_override == '1' ? true : false"
              :disabled="!isFormEditable"
              @update:model-value="changeOverrideTeacher"
            />
          </div>
        </div>
        <div class="mt-3 d-block" style="text-align: left">
          <CustomCheck
            v-model="state.form.kiosk_pass"
            label="KSK Passes students initiate at a Kiosk."
            :disabled="!isFormEditable"
            @change="state.form.kiosk_pass = !state.form.kiosk_pass"
            :invalid-feedback="
              errors.kiosk_pass || state.serverErrors.kiosk_pass
            "
          ></CustomCheck>
        </div>
      </fieldset>
      <div>
        <InfoBox class="mt-4" :use-live-region="false"
          >APT passes are not affected by this limit.<br />A "-" allows for
          unlimited passes.</InfoBox
        >
      </div>
      <div v-if="state.feedbackAlert.message" class="mt-4">
        <CAlert
          dismissible
          class="mb-0"
          :color="state.feedbackAlert.color"
          :show="true"
          :role="state.feedbackAlert.color === 'danger' ? 'alert' : 'status'"
          :aria-live="
            state.feedbackAlert.color === 'danger' ? 'assertive' : 'polite'
          "
          aria-atomic="true"
          data-testid="lap-form-feedback-alert"
          @close="dismissFeedbackAlert"
        >
          <div class="pe-4">
            <div v-if="state.feedbackAlert.title" class="fw-semibold mb-1">
              {{ state.feedbackAlert.title }}
            </div>
            <div class="mb-0">{{ state.feedbackAlert.message }}</div>
          </div>
        </CAlert>
      </div>
      <div v-if="isFormEditable">
        <LoadingButton
          :is-loading="state.isProcessing"
          @click="saveActivePassLimit()"
          class="mt-4 px-4"
          solid
          rounded
          >Save settings</LoadingButton
        >
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, computed, onMounted, watch } from "vue"
import { useStore } from "vuex"
import { helpers, required } from "@vuelidate/validators"
import useVuelidate from "@vuelidate/core"
import Label from "@/v3components/shared/Form/Label.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"

export default {
  name: "LimitActivePassForm",
  components: {
    Label,
    InputField,
    CustomCheck,
    CustomSwitch,
    InfoBox,
    LoadingButton
  },
  emits: ["cancel", "setActivePassLimit", "closeModal"],
  setup(props, { emit }) {
    const store = useStore()
    const state = reactive({
      isProcessing: false,
      serverErrors: {},
      feedbackAlert: {
        title: "",
        message: "",
        color: "success"
      },
      pendingCloseAfterDismiss: false,
      form: {
        limit: "",
        student_pass: false,
        proxy_pass: false,
        teacher_override: "0",
        kiosk_pass: false
      }
    })

    const activePassLimits = computed(
      () => store.getters["activePass/activePassLimits"]
    )

    const userRole = computed(() => store.getters["authentication/getUserRole"])

    const isFormEditable = computed(() => {
      return userRole.value === "admin"
    })

    watch(activePassLimits, (val) => {
      setFormData(val)
    })

    onMounted(() => {
      if (!activePassLimits.value) {
        store.dispatch("activePass/getActivePassLimits").then((response) => {
          const data = response.data.data
          setFormData(data)
        })
      } else {
        setFormData(activePassLimits.value)
      }
    })

    const setFormData = (data) => {
      if (data && Object.keys(data).length) {
        emit("setActivePassLimit", data.limit)
        state.form = {
          limit: data.limit === "" ? null : data.limit,
          student_pass: !!data.student_pass,
          proxy_pass: !!data.proxy_pass,
          kiosk_pass: !!data.kiosk_pass,
          teacher_override: data.teacher_override.toString()
        }
      } else {
        resetFormData()
      }
    }

    const setLimit = (value) => {
      const numbers = /^[0-9]+$/
      if (value) {
        if (value.match(numbers)) {
          state.form.limit = parseInt(value) > 0 ? parseInt(value) : 0
        } else {
          state.form.limit = value === "-" || value === "" ? value : ""
        }
      }
    }

    const changeOverrideTeacher = (enable) => {
      state.form.teacher_override = enable ? "1" : "0"
    }

    const validationMessages = {
      required: "This field is required"
    }
    const validations = {
      form: {
        limit: {
          required: helpers.withMessage(validationMessages.required, required)
        }
      }
    }
    const v$ = useVuelidate(validations.form, state.form)

    const isFormValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    const saveActivePassLimit = () => {
      if (isFormEditable.value) {
        state.isProcessing = true
        const data = {
          limit: state.form.limit === "" ? null : state.form.limit,
          student_pass: state.form.student_pass,
          proxy_pass: state.form.proxy_pass,
          teacher_override: state.form.teacher_override,
          kiosk_pass: state.form.kiosk_pass
        }
        store
          .dispatch("activePass/createActivePassLimit", data)
          .then(() => {
            setFeedbackAlert(
              "Success",
              "Limit settings successfully saved!",
              "success"
            )
            state.isProcessing = false
            state.serverErrors = {}
            state.pendingCloseAfterDismiss = true
            store.dispatch("activePass/getActivePassLimits")
          })
          .catch((err) => {
            const res = err.response.data
            state.serverErrors = res.errors ? res.errors : {}
            setFeedbackAlert("Error", res.message, "danger")
            state.isProcessing = false
          })
      }
    }

    const setFeedbackAlert = (title, message, color) => {
      if (title || message) {
        state.feedbackAlert = {
          title: title || "",
          message: message || "",
          color: color || "success"
        }
      } else {
        state.feedbackAlert = { title: "", message: "", color: "success" }
      }
    }

    const dismissFeedbackAlert = () => {
      const shouldClose = state.pendingCloseAfterDismiss
      state.pendingCloseAfterDismiss = false
      setFeedbackAlert()
      if (shouldClose) {
        emit("closeModal")
        emit("cancel")
      }
    }

    const resetFormData = () => {
      state.form.limit = null
      state.form.student_pass = false
      state.form.proxy_pass = false
      state.form.teacher_override = "0"
      state.form.kiosk_pass = false
    }

    return {
      state,
      isFormEditable,
      setLimit,
      changeOverrideTeacher,
      v$,
      isFormValid,
      errors,
      saveActivePassLimit,
      setFeedbackAlert,
      dismissFeedbackAlert,
      resetFormData
    }
  }
}
</script>

<style scoped lang="scss">
@import "src/assets/v3scss/_variables.scss";

.lap-teacher-override-hint {
  color: $gray-darken-40;
}

.limit-active-pass-form {
  :deep(.v3-custom-switch input:checked + .switch-toggler) {
    background-color: $blue-darken-10;
  }
}
</style>
